<template>
  <!-- Recipient journey Part 6, Stage 3: you and the sender. The Mystic's
       turns in the room, in order:
         1. the birthday, once (R6.3): the sender's guess as one tap (R6.4,
            SuggestedBirthday), or the birthday ask (R6.5, BirthAsk);
         2. her sun sign in one line tied to the card she pulled (R6.6);
         3. You & <sender> from the date alone (R6.7, OurStars), with the
            closer look inside it (R6.8);
         ("Your sign and the two of you are kept here." is gone: the gift
          room redo, 2026-10-29. The Mirror keeps them; step 7 links to it.)
       Integration: <StageThree :inv :from-name :name @open-mirror @reported /> in
       the chat (ChatContainerReader), started by the room (ReaderPage). -->
  <div class="s3" data-testid="stage-three">
    <template v-if="phase === 'suggest'">
      <div class="s3-turn s3-widget">
        <SuggestedBirthday :inv="inv" @confirmed="onConfirmed" @declined="phase = 'date'" @none="phase = 'date'" />
      </div>
    </template>

    <!-- The ask stays in the room once answered: the payoff is part of the talk. -->
    <template v-if="phase === 'date' || askedHere">
      <div class="s3-turn"><p class="s3-text" data-testid="s3-ask">{{ ask }}</p></div>
      <div class="s3-widget">
        <!-- The birthday ask (design 2026-10-26): the date, with a time and a
             place folded behind "I know my birth time too", each savable
             alone; then the payoff from the saved signs, and "See you &
             <sender>" carries on into the sign line and You & them. -->
        <BirthAsk mode="inline" context="gift" :with-name="fromName || 'them'" @saved="onSaved" @done="afterDate({ fromAsk: true })" />
      </div>
    </template>

    <template v-if="phase === 'after'">
      <div v-if="line && !replay" class="s3-turn"><p class="s3-text" data-testid="s3-sign-line">{{ line }}</p></div>
      <div v-if="lineDone" class="s3-widget">
        <OurStars :inv="inv" :with-name="fromName || 'them'" @done="onStars" />
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import BirthAsk from '@/components/widgets/BirthAsk.vue'
import OurStars from '@/components/widgets/OurStars.vue'
import SuggestedBirthday from '@/components/widgets/SuggestedBirthday.vue'
import { getBirthMoment, signLine } from '@/api-service'
import { birthdayAsk } from '@/utils/stage-three'

const props = defineProps({
  inv: { type: String, required: true },
  fromName: { type: String, default: '' },
  name: { type: String, default: '' },
  // The room opened again (a reload, another device; bug walk #6): You & the
  // sender was made before, so it is shown as it was, nothing asked, said or
  // started again.
  replay: { type: Boolean, default: false },
})
const emit = defineEmits(['open-mirror', 'reported', 'dated', 'settled'])

const phase = ref('loading')
const line = ref('')
const lineDone = ref(false)
const reported = ref(false)
const askedHere = ref(false)

const ask = computed(() => birthdayAsk(props.name))

onMounted(async () => {
  if (props.replay) { phase.value = 'after'; lineDone.value = true; return }
  let moment = null
  try { const r = await getBirthMoment(); moment = r && r.moment } catch (e) { moment = null }
  // A birthday she already gave is not asked again: what comes back comes now.
  if (moment && moment.birthdate) { afterDate(); return }
  phase.value = 'suggest'
})

function onConfirmed() { afterDate() }
function onSaved() { askedHere.value = true; emit('dated') }

async function afterDate({ fromAsk = false } = {}) {
  phase.value = 'after'
  if (!fromAsk) emit('dated')
  try {
    const r = await signLine(props.inv)
    line.value = (r && r.line) || ''
  } catch (e) { line.value = '' }
  lineDone.value = true
}

function onStars(r) {
  if (r && r.status === 'ready') {
    reported.value = true
    if (!props.replay) emit('reported', r.reading)
  }
  if (!props.replay) emit('settled', r && r.status)
}
</script>

<style scoped>
.s3 { display: flex; flex-direction: column; gap: 14px; margin: 14px 0; }
.s3-turn { position: relative; max-width: 88%; padding: 1px 0 1px 20px; }
.s3-turn::before { content: ''; position: absolute; left: 0; top: 4px; bottom: 4px; width: 2px; border-radius: 2px;
  background: linear-gradient(180deg, var(--cr-gold, #f0d9a3) 0%, var(--cr-gold-soft, rgba(240,217,163,0.4)) 38%, rgba(240, 217, 163, 0.05) 100%); }
.s3-text { margin: 0; color: var(--cr-ink, #efe6ff); font-family: var(--cr-serif, Georgia, serif); font-size: 19px; line-height: 1.62; }
.s3-widget { max-width: 94%; padding-left: 20px; }
.s3-turn.s3-widget::before { display: none; }
.s3-link { background: none; border: none; padding: 0; font: inherit; color: var(--cr-gold, #f0d9a3);
  text-decoration: underline; text-underline-offset: 4px; cursor: pointer; }
@media (max-width: 699px) {
  .s3-text { font-size: 17px; }
  .s3-turn, .s3-widget { max-width: 100%; padding-left: 15px; }
}
</style>
